import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ThemeToggle from "../../Pages/AiApp/ThemeToggle";
import { encryptData } from "../../Pages/Login/Login";
import { getThemeImage } from "../../Pages/Themes/ThemeSection";
import MaterialIcon from "../../components/BuilderComponents/MaterialIcon";
import Button from "../../components/FormComponents/Button";
import Input from "../../components/FormComponents/Input";
import { BASE_URL, IS_SANDBOX } from "../../constant/constant";
import { useAppOrgContext } from "../../context/AppOrgContext";
import { useAuth0Context } from "../../context/Auth0Context";
import { useModalContext } from "../../context/ModalContext";
import useClickOutside from "../../hooks/useClickOutside";
import websiteLogo from "../../images/zino-logo-orange.svg";
import axiosInstance, { handleApiError } from "../../services/axiosService";
import { toastSuccess } from "../../utils/toastUtils";
import { getToolTipAttributes } from "../../utils/tooltip";
import BuilderTabs from "../BuilderTabs";
import Loader from "../Loader/Loader";
import BuilderModal from "../Modal/BuilderModal";
import Popover from "../Popover/Popover";
import AppBlueprintTrees from "./AppBlueprintTrees";
import "./Header.css";
import ProfilePopover from "./ProfilePopover";
import TokenManager from "./TokenManager";

const Header = () => {
  const navigate = useNavigate();
  const { appOrgNames, applicationUrl, application } = useAppOrgContext();
  const { user, resetAuthState } = useAuth0Context();
  const [isThemeOpen, setIsThemeOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPassword, setIsPassword] = useState(false);
  const params = useParams();

  const [showOptions, setShowOptions] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [profileAnchor, setProfileAnchor] = useState(null);
  const popupOptionDivRef = useRef();
  const profileButtonRef = useRef();
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const timeoutRef = useRef(null);
  const location = useLocation();

  useClickOutside(popupOptionDivRef, () => setShowOptions(false));

  const handleLogout = async () => {
    try {
      await axiosInstance.post("/user/logout");
      resetAuthState();
    } catch (error) {
      handleApiError(error);
    }
  };

  const { getUserConfirmation } = useModalContext();

  const deployInSandbox = async () => {
    const userConfirmed = await getUserConfirmation(`Are You Sure, You want to deploy in Sandbox?`);
    if (userConfirmed) {
      await deployAction();
    }
  };

  const deployAction = async () => {
    try {
      const response = await axiosInstance.post(`/application/deploy/staging-env`, null);
      toastSuccess(response.message);
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsButtonDisabled(true);
      timeoutRef.current = setTimeout(() => {
        setIsButtonDisabled(false);
      }, 3000);
    }
  };

  const handleGeneratePdf = async () => {
    try {
      const { message } = await axiosInstance.get(`/app-document/${params.orgId}/${params.appId}`, {
        headers: {
          "Zino-App-Id": params.appId
        }
      });
      toastSuccess(message);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleProfileClick = (e) => {
    e.stopPropagation();

    const rect = profileButtonRef.current.getBoundingClientRect();
    const elementWidth = rect.width;

    setProfileAnchor({
      x: rect.left + elementWidth,
      y: rect.bottom
    });

    setShowProfile(true);
  };

  if (window.location.pathname.includes("aiapp")) {
    return null;
  }

  return (
    <header className="top_navbar_outerBox pl_0">
      <div className="top_navbar_logoBox">
        <div className="d_flex" onClick={() => navigate("/organization")}>
          <img src={websiteLogo} style={{ height: "18px" }} alt="App Studio" />
        </div>
        {params.orgId && appOrgNames.appName && !location.pathname.includes("/settings") && (
          <span onClick={() => navigate(`/organization/${params.orgId}/app`)} className="font_weight_normal">
            <span className="top_navbar_logoText app" style={{ color: "#f9fafb" }}>
              {params.appId && `${appOrgNames.appName}`}
            </span>
          </span>
        )}
      </div>

      <div className="top_navbar_right_wrapper">
        {params.appId && appOrgNames.orgName && (
          <div className="top_navbar_logoText org cursor_pointer" onClick={() => setIsModalOpen(true)}>
            {application && application?.logo ? (
              <img
                src={`${BASE_URL}/api/v1/public/${application.logo}`}
                alt="app-logo"
                style={{ width: "25px", height: "25px", borderRadius: "50%" }}
              />
            ) : params.appId && appOrgNames?.orgLogo ? (
              <img src={`${BASE_URL}/api/v1/public/${appOrgNames.orgLogo}`} alt="org-logo" style={{ width: "30px" }} />
            ) : (
              <MaterialIcon name="deployed_code" />
            )}
          </div>
        )}
        {params.orgId && appOrgNames.appName && (
          <div>
            <span className="top_navbar_logoText">{params.appId && `${appOrgNames.appName}`}</span>
            {params.appId && (
              <div className="top_org_name">
                <span onClick={() => navigate(`/organization/${params.orgId}/app`)} className="font_weight_normal">
                  {params.orgId && appOrgNames.orgName}
                </span>
              </div>
            )}
          </div>
        )}
        {params.appId && appOrgNames.appName && (
          <div
            className="menu_container"
            onClick={() => {
              setShowOptions(true);
            }}
            ref={popupOptionDivRef}
          >
            <MaterialIcon name="more_vert" className="nav_builder_icon" />
            {showOptions && (
              <div className="app_edit_btn_header">
                {applicationUrl.staging_status === "live" && params.appId && (
                  <>
                    <div className="popup_list_items nav_popup_list" onClick={(e) => e.stopPropagation()}>
                      <MaterialIcon name="desktop_windows" className="actions" />
                      <a
                        href={applicationUrl.staging_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="link_style w_100"
                      >
                        <span className="action_names">Staging Link </span>
                      </a>
                    </div>
                  </>
                )}
                {applicationUrl.status === "live" && params.appId && (
                  <>
                    <div className="popup_list_items nav_popup_list" onClick={(e) => e.stopPropagation()}>
                      <MaterialIcon name="screen_search_desktop" className="actions" />
                      <a
                        href={applicationUrl.web_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="link_style w_100"
                      >
                        <span className="action_names">Production Link</span>
                      </a>
                    </div>
                  </>
                )}
                <div className="popup_list_items  nav_popup_list" onClick={() => setIsThemeOpen(true)}>
                  <MaterialIcon name="palette" className="actions" />
                  <span className="action_names">Theme</span>
                </div>
                <div
                  title="Generate Requirement Pdf"
                  className="popup_list_items nav_popup_list"
                  onClick={handleGeneratePdf}
                >
                  <MaterialIcon name="notes" className="actions" />
                  <span className="action_names">Generate Req. Pdf</span>
                </div>
                <div className="popup_list_items nav_popup_list">
                  <TokenManager />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="d_flex" style={{ gap: "14px" }}>
        {params.appId && IS_SANDBOX && (
          <div className="d_flex">
            <Button
              size="small"
              {...getToolTipAttributes("Deploy", "bottom")}
              onClick={deployInSandbox}
              className="border_radius_small"
              disabled={isButtonDisabled || applicationUrl.staging_status !== "live"}
            >
              <MaterialIcon name="rocket_launch" className="action font_size_large" />
            </Button>
          </div>
        )}
        {!params.appId && (
          <>
            <ThemeToggle rotate={false} />
            <div ref={profileButtonRef} className="profile-dropdown-container" onClick={handleProfileClick}>
              <MaterialIcon name="person" className="top_navbar_profile_icon" />
              {showProfile && profileAnchor && (
                <Popover anchorPoint={profileAnchor} onClose={() => setShowProfile(false)}>
                  <ProfilePopover
                    user={user}
                    onChangePassword={() => {
                      setShowProfile(false);
                      setIsPassword(true);
                    }}
                    onLogout={handleLogout}
                  />
                </Popover>
              )}
            </div>
          </>
        )}
      </div>

      <BuilderModal isOpen={isThemeOpen} onClose={() => setIsThemeOpen(false)} modalName="Themes">
        <ThemeModal setIsThemeOpen={setIsThemeOpen} />
      </BuilderModal>
      <BuilderModal isOpen={isPassword} onClose={() => setIsPassword(false)} modalName="Change Password">
        <ChangePassword setIsPassword={setIsPassword} />
      </BuilderModal>
      <BuilderModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} modalName="App blueprint">
        <AppBlueprintTrees setIsModalOpen={setIsModalOpen} />
      </BuilderModal>
    </header>
  );
};

const ChangePassword = ({ setIsPassword }) => {
  const [passwordData, setPasswordData] = useState({
    existingpassword: "",
    newPassword: "",
    reEnteredPassword: ""
  });
  const [passError, setPassError] = useState("");

  const handlePasswordChange = async () => {
    try {
      if (passwordData.newPassword.length < 4) {
        setPassError("length");
        return;
      }
      if (passwordData.newPassword !== passwordData.reEnteredPassword) {
        setPassError("new");
        return;
      }
      const { message } = await axiosInstance.put(`/user/loginPassword`, {
        existingpassword: encryptData(passwordData.existingpassword),
        newPassword: encryptData(passwordData.newPassword)
      });
      toastSuccess(message);
      setIsPassword(false);
    } catch (error) {
      setPassError("old");
      handleApiError(error);
    }
  };

  const handleInputChange = (e) => {
    setPassError("");
    const { name, value } = e.target;
    setPasswordData((prevData) => ({
      ...prevData,
      [name]: value
    }));
  };

  return (
    <div className="builder_form">
      <Input
        label="Existing Password"
        type="password"
        placeholder="password"
        name="existingpassword"
        value={passwordData.existingpassword}
        onChange={handleInputChange}
      />
      {passError === "old" && <label className="builder_label label_error">Incorrect Password</label>}

      <Input
        label="New Password"
        type="password"
        placeholder="password"
        name="newPassword"
        value={passwordData.newPassword}
        onChange={handleInputChange}
      />
      {passError === "length" && (
        <label className="builder_label label_error">Password should be atleast 4 digits</label>
      )}

      <Input
        label="Re-enter New Password"
        type="password"
        placeholder="password"
        name="reEnteredPassword"
        value={passwordData.reEnteredPassword}
        onChange={handleInputChange}
      />
      {passError === "new" && <label className="builder_label label_error">Password did not match</label>}

      <Button onClick={handlePasswordChange}>Change Password</Button>
    </div>
  );
};

const ThemeModal = ({ setIsThemeOpen }) => {
  const [activeTab, setActiveTab] = useState("desktop");
  const { application, setApplication } = useAppOrgContext();
  const { setSaveLoader } = useModalContext();
  const [themes, setThemes] = useState([]);
  const [selectedTheme, setSelectedTheme] = useState("");
  const [hoveredTheme, setHoveredTheme] = useState(null);
  const [isLoader, setIsLoader] = useState(false);
  const { orgId, appId } = useParams();
  useEffect(() => {
    let baseTab = "mobile";
    if (activeTab.includes("desktop")) {
      baseTab = "desktop";
    } else if (activeTab.includes("tablet")) {
      baseTab = "tablet";
    }

    setSelectedTheme(application?.[`${baseTab}_theme_id`] || "");
  }, [activeTab, application]);

  const tabs = [
    { label: "Desktop", content: null },
    { label: "Mobile", content: null },
    { label: "Tablet", content: null },
    { label: "Org Desktop", content: null },
    { label: "Org Mobile", content: null },
    { label: "Org Tablet", content: null }
  ];

  const getThemes = async () => {
    try {
      setIsLoader(true);
      const { data } = await axiosInstance.get(`sysconfigs/type/theme`);

      // Sorting themes alphabetically
      data.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));

      setThemes(data);
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoader(false);
    }
  };

  const updateTheme = async (theme) => {
    try {
      setSaveLoader(true);
      const updatedThemeId = theme?.id || "";
      let baseTab = "mobile";
      if (activeTab.includes("desktop")) {
        baseTab = "desktop";
      } else if (activeTab.includes("tablet")) {
        baseTab = "tablet";
      }
      const config = {
        [`${baseTab}_theme_id`]: updatedThemeId
      };
      const { message } = await axiosInstance.put(`/organisations/${orgId}/apps/${appId}`, config);

      setSelectedTheme(updatedThemeId);
      setApplication({
        ...application,
        [`${baseTab}_theme_id`]: updatedThemeId
      });
      // if (screenId) {
      //   fetchAndApplyTheme(selectedScreen.parentData.split("/")[0], updatedThemeId);
      // }
      // if (blockId) {
      //   fetchAndApplyTheme(selectedAppBlock.parentData.split("/")[0], updatedThemeId);
      // }
      setIsThemeOpen(false);
      toastSuccess(message);
    } catch (error) {
      handleApiError(error);
    } finally {
      setSaveLoader(false);
    }
  };

  useEffect(() => {
    getThemes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredThemes = themes.filter((theme) => {
    if (activeTab.startsWith("org_")) {
      return theme.org_id === orgId && theme.config.device === activeTab;
    }
    return theme.config.device === activeTab;
  });

  return (
    <div>
      <BuilderTabs
        tabs={tabs}
        onChange={(tabLabel) => setActiveTab(tabLabel.toLowerCase().replace(" ", "_"))}
        tabsClass="theme_tab"
        containerClass="theme_tabs"
      />
      <div className="theme_modal">
        {isLoader ? (
          <Loader />
        ) : (
          <>
            {filteredThemes?.length === 0 && (
              <h2 className="mx_auto d_flex justify_center align_center h_100">No Themes Found</h2>
            )}

            {/* Theme Grid */}
            <div className="themes_grid">
              {filteredThemes.map((theme) => (
                <div
                  key={theme.id}
                  className={`theme_card ${selectedTheme === theme.id ? "selected" : ""}`}
                  onMouseEnter={() => setHoveredTheme(theme.id)}
                  onMouseLeave={() => setHoveredTheme(null)}
                >
                  <div className="theme_image">
                    <div className="theme_actions">
                      {/* Always show "Applied" on the selected theme */}
                      {selectedTheme === theme.id ? (
                        <Button
                          variant="secondary"
                          size="medium"
                          className="applied_theme_button"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px"
                          }}
                        >
                          <span
                            style={{
                              width: "20px",
                              height: "20px",
                              backgroundColor: "#37cc5f",
                              color: "white",
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "14px",
                              fontWeight: "bold"
                            }}
                          >
                            ✓
                          </span>
                          Applied
                        </Button>
                      ) : (
                        // Show "Apply Theme" button when hovering over an unselected theme
                        hoveredTheme === theme.id && (
                          <Button
                            onClick={() => updateTheme(theme)}
                            variant="secondary"
                            size="medium"
                            className="apply_theme_button"
                          >
                            Apply Theme
                          </Button>
                        )
                      )}
                    </div>
                    <div className="overlay_cover"></div>
                    <img src={getThemeImage(theme.name)} alt={theme.name} className="theme_image" />
                    <hr className="light-line"></hr>
                  </div>
                  <div className="theme_name">{theme.name}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Header;
